import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { RootState } from "../../app/store";
import { CollectionAccount } from "./models/CollectionAccount";
import { DebitMandate } from "./models/DebitMandate";
import {
  fetchCollectionAccount,
  fetchMandates,
  refreshCollectionAccount,
} from "./slice-extensions/collection-slice-extension";

interface CollectionState {
  account?: CollectionAccount;
  accountStatus: LoadingStatus;
  mandatesByMemberId: Record<string, DebitMandate>;
  mandatesStatus: LoadingStatus;
  lastError?: ApiErrorResponse;
}

const initialState: CollectionState = {
  account: undefined,
  accountStatus: LoadingStatus.Idle,
  mandatesByMemberId: {},
  mandatesStatus: LoadingStatus.Idle,
  lastError: undefined,
};

const applyAccount = (state: CollectionState, action: PayloadAction<CollectionAccount>) => {
  state.accountStatus = LoadingStatus.Idle;
  state.account = action.payload;
};

const collectionSlice = createSlice({
  name: "collection",
  initialState,
  reducers: {
    clearCollectionError: (state) => {
      state.lastError = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCollectionAccount.pending, (state) => {
        state.accountStatus = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(fetchCollectionAccount.fulfilled, applyAccount)
      .addCase(fetchCollectionAccount.rejected, (state, action) => {
        state.accountStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      })

      .addCase(refreshCollectionAccount.pending, (state) => {
        state.accountStatus = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(refreshCollectionAccount.fulfilled, applyAccount)
      .addCase(refreshCollectionAccount.rejected, (state, action) => {
        state.accountStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      })

      .addCase(fetchMandates.pending, (state) => {
        state.mandatesStatus = LoadingStatus.Loading;
      })
      .addCase(fetchMandates.fulfilled, (state, action: PayloadAction<DebitMandate[]>) => {
        state.mandatesStatus = LoadingStatus.Idle;
        state.mandatesByMemberId = Object.fromEntries(
          action.payload.map((mandate) => [mandate.memberId, mandate])
        );
      })
      .addCase(fetchMandates.rejected, (state, action) => {
        state.mandatesStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      });
  },
});

export const { clearCollectionError } = collectionSlice.actions;

export const selectCollectionAccount = (state: RootState) => state.collection.account;
export const selectCollectionAccountStatus = (state: RootState) => state.collection.accountStatus;
export const selectMandateForMember = (memberId: string) => (state: RootState) =>
  state.collection.mandatesByMemberId[memberId];
export const selectMandatesStatus = (state: RootState) => state.collection.mandatesStatus;
export const selectCollectionError = (state: RootState) => state.collection.lastError;

export default collectionSlice.reducer;
